// Turning an existing connection back into a form draft.
//
// Editing a connection is not a new mechanism. Previewing a spec that carries
// an existing connector ID already produces revision N+1 of that connector,
// the create form already accepts an `initialDraft` with a `connector_id`, and
// the review-and-confirm step is unchanged. The only piece that was missing is
// this one: turning what the device is currently running back into the shape
// the form was filled in with.
//
// Everything here comes from `connector.settings`, the redacted view built in
// core/scada/edgerelayserver/server_connector_settings.go. That endpoint
// decides which kinds can be edited at all — a kind whose settings cannot be
// read back in full reports `editable: false` with a reason, because a form
// that quietly defaults a field the operator never saw, and then writes that
// default to live equipment, is worse than having no Edit button. This module
// trusts that flag rather than second-guessing it.

// canEditConnector mirrors the server's decision. The reason string is written
// for an operator and is safe to show as-is.
const points = (connector) => (Array.isArray(connector?.settings?.points) ? connector.settings.points : []);

const desiredState = (connector) => {
  const state = String(connector?.desired_state || "").trim().toLowerCase();
  return state === "running" || state === "stopped" ? state : "";
};

export const canEditConnector = (connector) => Boolean(
  connector?.settings?.editable &&
  desiredState(connector) &&
  (connector?.kind !== "gpio" || points(connector).length === 1)
);

export const editConnectorReason = (connector) => {
  if (connector?.settings?.editable && !desiredState(connector)) {
    return "Refresh this connection before changing it.";
  }
  if (connector?.kind === "gpio" && connector?.settings?.editable && points(connector).length !== 1) {
    return "This digital I/O connection cannot be changed in place. Remove it and connect it again.";
  }
  return String(connector?.settings?.reason || "");
};

// editConnectorKind maps a stored connector kind onto the form's protocol
// value. Modbus is one stored kind with two form kinds, split by transport,
// because the form asks completely different questions for a serial bus than
// for a TCP host.
export const editConnectorKind = (connector) => {
  switch (connector?.kind) {
    case "modbus":
      return connector?.settings?.modbus?.transport === "rtu"
        ? "modbus-rtu"
        : connector?.settings?.modbus?.transport === "rtu_over_tcp" ? "modbus-rtu-over-tcp" : "modbus-tcp";
    case "gpio":
      return "gpio";
    case "ncd-pr63-27c-usb":
    case "ncd-pr63-27c-ethernet":
      return "ncd-pr63-27c-usb";
    default:
      return "";
  }
};

const ncdDraft = (connector) => {
  const receiver = connector?.settings?.ncd_receiver;
  if (!receiver) return null;
  const transport = String(receiver.transport || (connector?.kind === "ncd-pr63-27c-ethernet" ? "ethernet" : "usb"));
  return {
    connector_id: connector.connector_id,
    expected_revision: connector.desired_revision,
    desired_state: desiredState(connector),
    name: connector.name || "",
    settings: {
      transport,
      apiMode: Number(receiver.api_mode || 1),
      ...(transport === "ethernet" ? {
        autoDiscover: Boolean(receiver.auto_discover),
        host: String(receiver.host || ""),
        port: Number(receiver.port || 2101),
      } : {
        serialPort: String(receiver.serial_port || ""),
        baudRate: Number(receiver.baud_rate || 115200),
      }),
    },
  };
};

// gpioDraft rebuilds a single-line GPIO draft.
//
const gpioDraft = (connector) => {
  if (points(connector).length !== 1) return null;
  const point = points(connector)[0];
  if (!point?.gpio) return null;
  return {
    connector_id: connector.connector_id,
    expected_revision: connector.desired_revision,
    desired_state: desiredState(connector),
    name: connector.name || "",
    tag_mode: "existing",
    tag_path: point.tag_path,
    direction: point.gpio.direction === "output" ? "output" : "input",
    bcm_pin: point.gpio.bcm_pin,
    bias: point.gpio.bias,
    debounce_ms: point.gpio.debounce_ms,
    active_low: point.gpio.active_low,
    safe_state: point.gpio.safe_state,
    // Keep the exact profile that produced the signed mapping. The form also
    // compares it with current runtime discovery before allowing an edit, so
    // replacing hardware cannot silently reinterpret the same pin number.
    settings: { boardProfile: String(connector?.settings?.gpio?.board_profile || "") },
  };
};

// modbusDraft rebuilds a Modbus device draft with all of its points.
//
// deviceTemplate is not recoverable and is always reopened as "generic". It is
// a property of the form rather than of the connection — the Turck template is
// a shortcut that fills in a function code and turns a channel number into a
// register bit — and every value it produced is carried here explicitly. So a
// connection created from the template reopens in the generic editor showing
// the same settings it is actually running, and saving reproduces the same
// spec. The shortcut is lost; nothing about the device is.
const modbusDraft = (connector) => {
  const device = connector?.settings?.modbus;
  if (!device) return null;
  const asString = (value) => (value == null ? "" : String(value));
  return {
    connector_id: connector.connector_id,
    expected_revision: connector.desired_revision,
    desired_state: desiredState(connector),
    name: connector.name || "",
    settings: {
      deviceTemplate: "generic",
      host: asString(device.host),
      port: asString(device.port || 502),
      serialPort: asString(device.serial_port),
      baudRate: asString(device.baud_rate || 19200),
      dataBits: asString(device.data_bits || 8),
      stopBits: asString(device.stop_bits || 1),
      parity: asString(device.parity || "even"),
      unitId: asString(device.unit_id),
      timeoutMs: asString(device.timeout_ms),
      reconnectMinMs: asString(device.reconnect_min_ms),
      reconnectMaxMs: asString(device.reconnect_max_ms),
    },
    points: points(connector).map((point) => ({
      tag_mode: "existing",
      tag_path: point.tag_path,
      settings: {
        function: point.modbus?.function == null ? "" : String(point.modbus.function),
        address: asString(point.modbus?.address ?? 0),
        valueType: asString(point.modbus?.value_type),
        bit: asString(point.modbus?.bit ?? 0),
        byteOrder: asString(point.modbus?.byte_order || "big"),
        wordOrder: asString(point.modbus?.word_order || "big"),
        scale: asString(point.modbus?.scale ?? 1),
        offset: asString(point.modbus?.offset ?? 0),
        pollIntervalMs: asString(point.modbus?.poll_interval_ms ?? 100),
        maxReadGap: asString(point.modbus?.max_read_gap ?? 0),
        maxReadCount: asString(point.modbus?.max_read_count ?? ""),
        failurePolicy: asString(point.modbus?.failure_policy),
        writeConfirmation: asString(point.modbus?.write_confirmation),
        readWindowMode: point.modbus?.read_window_start != null || point.modbus?.read_window_count != null ? "fixed" : "automatic",
        readWindowStart: asString(point.modbus?.read_window_start ?? point.modbus?.address ?? 0),
        readWindowCount: asString(point.modbus?.read_window_count ?? 1),
        stringLength: asString(point.modbus?.string_length ?? 32),
        stringRaw: Boolean(point.modbus?.string_raw),
        stringRightJustify: Boolean(point.modbus?.string_right_justify),
        alias: asString(point.modbus?.alias),
        // Flow is an authority boundary. Preserve exactly what the operator
        // approved so changing a timeout cannot turn a monitor into output.
        flow: asString(point.flow),
      },
    })),
  };
};

// edgeConnectorEditDraft returns the form draft for an existing connection, or
// null when this connection cannot be edited. A null result must leave the
// Edit action hidden rather than opening an empty form.
export const edgeConnectorEditDraft = (connector) => {
  if (!canEditConnector(connector)) return null;
  switch (connector?.kind) {
    case "gpio":
      return gpioDraft(connector);
    case "modbus":
      return modbusDraft(connector);
    case "ncd-pr63-27c-usb":
    case "ncd-pr63-27c-ethernet":
      return ncdDraft(connector);
    default:
      return null;
  }
};
